import { v4 as uuidv4 } from 'uuid'
import { districts } from 'constants/districts'
import { departments } from 'configs/SectionsConfig'

// Realistic demo data generator (ТЗ п.8). Produces plausible numbers for all
// 13 districts across 3 periods (Вопрос 8Б), statistics upload history, and
// 2–3 seeded department documents per subsection (depts 1–4) with placeholder
// file bytes so the "download" action works uniformly.

export const PERIODS = ['2023-2024', '2024-2025', '2025-2026']

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)]
const chance = (p) => Math.random() < p

// Random ISO datetime within the last `days` days.
const recentDate = (days = 180) => {
	const now = Date.now()
	const t = now - randInt(0, days) * 86400000 - randInt(0, 86400) * 1000
	return new Date(t).toISOString()
}

const MODULE_SCHOOLS = 'talim-statistikasi-maktablar'
const MODULE_TEACHERS = 'talim-statistikasi-oqituvchilar'

const DOC_WORDS = ['hisobot', 'bayonnoma', 'shartnoma', 'buyruq', 'reja', 'tahlil', 'ma’lumotnoma', 'xat']
const DOC_EXT = ['xlsx', 'pdf', 'docx']

const placeholderBlob = (fileName) => {
	const text = `DEMO fayl — ${fileName}\nBu namunaviy hujjat (demo rejimi).`
	return new Blob([text], { type: 'text/plain' })
}

const buildStats = () => {
	const schoolsStats = []
	const teachersStats = []

	districts.forEach((d) => {
		const isCity = d.type === 'shahar'
		// per-district base sizes (stable across the 3 periods, scaled by growth)
		const basePublic = isCity ? randInt(30, 70) : randInt(15, 40)
		const basePrivate = isCity ? randInt(2, 8) : randInt(0, 5)
		const avgPerSchool = randInt(400, 800)

		PERIODS.forEach((period, i) => {
			// slight year-on-year growth: older periods a bit smaller
			const growth = 0.9 + i * 0.05 // 0.90, 0.95, 1.00
			const publicSchools = Math.max(1, Math.round(basePublic * growth))
			const privateSchools = Math.max(0, Math.round(basePrivate * growth))
			const totalStudents = Math.round(
				(publicSchools + privateSchools) * avgPerSchool * growth
			)
			// boys/girls ~ 51/49 with small noise
			const boyShare = 0.51 + (Math.random() - 0.5) * 0.02
			const boys = Math.round(totalStudents * boyShare)
			const girls = totalStudents - boys

			const sourceFile = `${d.id}_maktablar_${period.slice(0, 4)}.xlsx`
			schoolsStats.push({
				id: `${d.id}__${period}`,
				districtId: d.id,
				period,
				privateSchools,
				publicSchools,
				totalStudents,
				boys,
				girls,
				uploadedAt: recentDate(),
				sourceFile,
			})

			// teachers: ~1 per 12–16 students
			const totalTeachers = Math.round(totalStudents / randInt(12, 16))
			const withCertificate = Math.round(totalTeachers * (0.55 + Math.random() * 0.2))
			const withPhD = isCity ? randInt(6, 20) : randInt(2, 10)
			teachersStats.push({
				id: `${d.id}__${period}`,
				districtId: d.id,
				period,
				totalTeachers,
				withCertificate,
				withPhD,
				uploadedAt: recentDate(),
				sourceFile: `${d.id}_oqituvchilar_${period.slice(0, 4)}.xlsx`,
			})
		})
	})

	return { schoolsStats, teachersStats }
}

const buildUploadHistory = () => {
	const uploadHistory = []
	const latest = PERIODS[PERIODS.length - 1]

	// one success record per district for each statistics module (latest period)
	districts.forEach((d) => {
		uploadHistory.push({
			id: uuidv4(),
			moduleId: MODULE_SCHOOLS,
			fileName: `${d.id}_maktablar_${latest.slice(0, 4)}.xlsx`,
			districtId: d.id,
			uploadedAt: recentDate(120),
			status: 'success',
			rowsCount: 1,
			validationErrors: [],
		})
		uploadHistory.push({
			id: uuidv4(),
			moduleId: MODULE_TEACHERS,
			fileName: `${d.id}_oqituvchilar_${latest.slice(0, 4)}.xlsx`,
			districtId: d.id,
			uploadedAt: recentDate(120),
			status: 'success',
			rowsCount: 1,
			validationErrors: [],
		})
	})

	// a couple of error samples for realism
	uploadHistory.push({
		id: uuidv4(),
		moduleId: MODULE_SCHOOLS,
		fileName: 'vobkent_maktablar_xato.xlsx',
		districtId: 'vobkent',
		uploadedAt: recentDate(90),
		status: 'error',
		rowsCount: 0,
		validationErrors: ["Noma'lum tuman: 'Vobkent shahri'"],
	})
	uploadHistory.push({
		id: uuidv4(),
		moduleId: MODULE_TEACHERS,
		fileName: 'olot_oqituvchilar_xato.xlsx',
		districtId: 'olot',
		uploadedAt: recentDate(60),
		status: 'error',
		rowsCount: 0,
		validationErrors: ['Sertifikatga ega > Jami o‘qituvchilar'],
	})

	return uploadHistory
}

const buildDepartmentDocuments = () => {
	const departmentDocuments = []
	const files = []

	// depts 1–4 only (statistics dept has its own strict module)
	departments
		.filter((dept) => !dept.statistics)
		.forEach((dept) => {
			dept.subsections.forEach((sub) => {
				const count = randInt(2, 3)
				for (let n = 0; n < count; n++) {
					const isError = chance(0.15)
					const fileName = `${sub.code}_${pick(DOC_WORDS)}_${randInt(1, 99)}.${pick(DOC_EXT)}`
					const doc = {
						id: uuidv4(),
						departmentId: dept.key,
						subsectionId: sub.code,
						fileName,
						uploadedAt: recentDate(),
						status: isError ? 'error' : 'success',
						note: '',
						fileId: null,
					}
					if (!isError) {
						const fileId = uuidv4()
						files.push({
							id: fileId,
							blob: placeholderBlob(fileName),
							name: fileName,
							type: 'text/plain',
						})
						doc.fileId = fileId
					}
					departmentDocuments.push(doc)
				}
			})
		})

	return { departmentDocuments, files }
}

export const generateDemoData = () => {
	const { schoolsStats, teachersStats } = buildStats()
	const uploadHistory = buildUploadHistory()
	const { departmentDocuments, files } = buildDepartmentDocuments()
	return { schoolsStats, teachersStats, uploadHistory, departmentDocuments, files }
}

export default generateDemoData
